// Installation obligatoire sur téléphone, seulement en mode panoramique : tant que le jeu n'est pas lancé depuis l'écran d'accueil
// (version installée), une fenêtre bloquante demande de l'installer pour jouer en mode panoramique. En portrait, rien n'est exigé :
// la fenêtre apparaît quand le téléphone passe en paysage (data-layout='landscape') et disparaît quand il revient en portrait.
// Ni croix, ni fond, ni Échap : on en sort en installant puis en ouvrant l'appli installée, ou en repassant en portrait. Le bouton déclenche l'invite native (Android) ou l'aide pas à
// pas (iOS Safari, voir ui/install.js) ; quand le navigateur ne propose rien, une phrase explique le geste à la main.
import { $ } from './dom.js';
import { createDialog } from './dialog.js';
import { manualInstallHelp } from '../core/install.js';

/**
 * @param {{ installOffer: ReturnType<typeof import('./install.js').createInstallOffer>, t: (key: string) => string, userAgent?: string, diagnostics?: string|null }} deps
 *   diagnostics : texte des mesures de l'appareil, affiché sous le message avec ?install=debug (pour comprendre pourquoi un appareil est ou n'est pas bloqué)
 */
export function createInstallGate({ installOffer, t, userAgent = navigator.userAgent, diagnostics = null }) {
  const message = $('installGateMessage');
  const button = $('btnInstallGate');
  const note = $('installGateNote');

  function render() {
    const installed = installOffer.isInstalled();
    const mode = installOffer.getMode();
    message.textContent = t(installed ? 'install.gate.installed' : 'install.gate.message');
    button.hidden = installed || mode === 'none';
    const help = !installed && mode === 'none'
      ? t(manualInstallHelp(userAgent) === 'ios-other' ? 'install.gate.manualIosOther' : 'install.gate.manualGeneric')
      : '';
    note.textContent = [help, diagnostics].filter(Boolean).join('\n');
    note.hidden = !note.textContent;
  }

  const dialog = createDialog($('installGateOverlay'), { onOpen: render });
  button.addEventListener('click', () => { installOffer.trigger(); });
  installOffer.onChange(render); // invite native détectée après coup, ou appli installée

  return { open: () => dialog.open({ dismissible: false }), close: () => dialog.close(null), isOpen: () => dialog.isOpen() };
}

/**
 * Montre la fenêtre tant que la mise en page est horizontale (attribut `data-layout` de <html>, voir ui/layoutMode.js), la retire
 * au retour en portrait.
 * @param {{ open: () => void, close: () => void }} gate
 * @param {{ root?: HTMLElement, onBlock?: () => void, onUnblock?: () => void }} [hooks]
 *   onBlock / onUnblock : le jeu se met en pause pendant que la fenêtre est affichée, et reprend ensuite
 */
export function guardLandscape(gate, { root = document.documentElement, onBlock, onUnblock } = {}) {
  let blocking = false;
  let waiting = [];

  function sync() {
    const landscape = root.getAttribute('data-layout') === 'landscape';
    if (landscape && !blocking) {
      blocking = true;
      onBlock?.();
      gate.open();
    } else if (!landscape && blocking) {
      blocking = false;
      gate.close();
      onUnblock?.();
      const done = waiting;
      waiting = [];
      done.forEach((resolve) => resolve());
    }
  }

  const observer = new MutationObserver(sync);
  observer.observe(root, { attributes: true, attributeFilter: ['data-layout'] });
  sync();

  return {
    sync,
    isBlocking: () => blocking,
    /** Résolue quand la fenêtre n'est pas (ou plus) affichée : tout de suite en portrait. */
    whenFree: () => (blocking ? new Promise((resolve) => { waiting.push(resolve); }) : Promise.resolve()),
    destroy: () => observer.disconnect(),
  };
}
